import React, { useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, TextField, Button, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, Avatar, Alert, Snackbar, Chip, Divider, Paper,
  Tabs, Tab, IconButton, useTheme, InputAdornment
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Business, Palette, FontDownload, PhoneAndroid, Save, ColorLens,
  Image as ImageIcon, Storage as StorageIcon
} from '@mui/icons-material';

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Nunito', label: 'Nunito' },
  { value: 'Raleway', label: 'Raleway' },
];

const SCREEN_CONFIGS = [
  { key: 'showHistory', label: 'Historial de pedidos' },
  { key: 'showWallet', label: 'Billetera / Créditos' },
  { key: 'showProfile', label: 'Perfil de usuario' },
  { key: 'showDeliveryCliente', label: 'Delivery Cliente' },
  { key: 'showDeliveryComercio', label: 'Delivery Comercio' },
  { key: 'showDeliveryMensajero', label: 'Delivery Mensajero' },
  { key: 'showNotifications', label: 'Notificaciones' },
  { key: 'showSupport', label: 'Centro de soporte' },
  { key: 'showReports', label: 'Reportes y estadísticas' },
  { key: 'showPrices', label: 'Consulta de precios' },
  { key: 'showQueue', label: 'Cola de mensajeros' },
  { key: 'showSettlements', label: 'Liquidaciones' },
  { key: 'showMap', label: 'Mapa de seguimiento' },
  { key: 'showDocuments', label: 'Gestión de documentos' },
];

const fetchtTenants = async () => {
  const res = await fetch('/api/superadmin/tenants', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  return res.json();
};

export default function SuperAdminAppBranding() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [config, setConfig] = useState({
    color_primario: '#1976d2',
    color_secundario: '#dc004e',
    mensajero_color_primario: '#2e7d32',
    mensajero_color_secundario: '#8bc34a',
    logo_url: '',
    mensajero_logo_url: '',
    app_background_color: '#ffffff',
    app_background_image_url: '',
    font_family: 'Inter',
    app_screens_config: {}
  });

  const { data: tenantsData } = useQuery('tenants', fetchtTenants);
  const tenants = tenantsData?.data || [];

  const selectedTenant = tenants.find(t => t.id === parseInt(selectedTenantId));

  const handleSelectTenant = (id) => {
    setSelectedTenantId(id);
    const t = tenants.find(t => t.id === parseInt(id));
    if (t) {
      setConfig({
        color_primario: t.color_primario || '#1976d2',
        color_secundario: t.color_secundario || '#dc004e',
        mensajero_color_primario: t.mensajero_color_primario || '#2e7d32',
        mensajero_color_secundario: t.mensajero_color_secundario || '#8bc34a',
        logo_url: t.logo_url || '',
        mensajero_logo_url: t.mensajero_logo_url || '',
        app_background_color: t.app_background_color || '#ffffff',
        app_background_image_url: t.app_background_image_url || '',
        font_family: t.font_family || 'Inter',
        app_screens_config: t.app_screens_config || {},
      });
    }
  };

  const handleSave = async () => {
    if (!selectedTenantId) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/superadmin/tenants/${selectedTenantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(config),
      });
      const data = await res.json();
      if (data.success) {
        setSnack({ open: true, msg: 'Configuración guardada exitosamente', severity: 'success' });
        queryClient.invalidateQueries('tenants');
      } else {
        setSnack({ open: true, msg: data.error || 'Error al guardar', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: 'Error de conexión', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <PhoneAndroid sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Personalización de Apps</Typography>
        <Chip label="Marca Blanca" color="primary" variant="outlined" size="small" />
      </Box>

      <Grid container spacing={3}>
        {/* Tenant Selector */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <FormControl fullWidth>
                <InputLabel>Seleccionar Empresa</InputLabel>
                <Select
                  value={selectedTenantId} label="Seleccionar Empresa"
                  onChange={e => handleSelectTenant(e.target.value)}
                >
                  {tenants.filter(t => t.estado === 'activo').map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {selectedTenant && (
          <>
            {/* Preview Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'sticky', top: 24 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Vista Previa</Typography>
                  <Paper
                    sx={{
                      p: 3, borderRadius: 3, textAlign: 'center',
                      bgcolor: config.app_background_color || '#fff',
                      backgroundImage: config.app_background_image_url ? `url(${config.app_background_image_url})` : 'none',
                      backgroundSize: 'cover',
                      minHeight: 300,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2
                    }}
                  >
                    <Avatar src={config.logo_url || undefined} sx={{ width: 72, height: 72, bgcolor: config.color_primario }}>
                      <Business />
                    </Avatar>
                    <Typography variant="h6" fontWeight={700} sx={{ fontFamily: config.font_family }}>
                      {selectedTenant.nombre}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                      <Chip size="small" label="Admin" sx={{ bgcolor: config.color_primario, color: '#fff' }} />
                      <Chip size="small" label="Mensajero" sx={{ bgcolor: config.mensajero_color_primario, color: '#fff' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Fuente: {config.font_family}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>

            {/* Config Form */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
                    <Tab icon={<Palette />} label="Colores" />
                    <Tab icon={<ImageIcon />} label="Logos / Fondo" />
                    <Tab icon={<FontDownload />} label="Tipografía" />
                    <Tab icon={<StorageIcon />} label="Pantallas" />
                  </Tabs>

                  {tab === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Colores de la App</Typography>
                      <Grid container spacing={2}>
                        {[
                          { label: 'Color Primario', key: 'color_primario' },
                          { label: 'Color Secundario', key: 'color_secundario' },
                          { label: 'Mensajero Primario', key: 'mensajero_color_primario' },
                          { label: 'Mensajero Secundario', key: 'mensajero_color_secundario' },
                          { label: 'Fondo App', key: 'app_background_color' },
                        ].map(f => (
                          <Grid item xs={12} sm={6} key={f.key}>
                            <TextField
                              fullWidth label={f.label} size="small"
                              value={config[f.key]}
                              onChange={e => setConfig(c => ({ ...c, [f.key]: e.target.value }))}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: config[f.key], border: '1px solid #ddd', mr: 0.5 }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {tab === 1 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Logos e Imagen de Fondo</Typography>
                      {[
                        { label: 'Logo App Admin', key: 'logo_url', placeholder: 'https://...' },
                        { label: 'Logo App Mensajero', key: 'mensajero_logo_url', placeholder: 'https://...' },
                        { label: 'Imagen de Fondo', key: 'app_background_image_url', placeholder: 'https://...' },
                      ].map(f => (
                        <TextField key={f.key} fullWidth label={f.label} size="small" placeholder={f.placeholder}
                          value={config[f.key]}
                          onChange={e => setConfig(c => ({ ...c, [f.key]: e.target.value }))}
                        />
                      ))}
                      <Typography variant="caption" color="text.secondary">
                        Sube las imágenes desde el módulo de archivos y pega aquí la URL.
                      </Typography>
                    </Box>
                  )}

                  {tab === 2 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Tipografía</Typography>
                      <FormControl fullWidth>
                        <InputLabel>Fuente de la App</InputLabel>
                        <Select value={config.font_family} label="Fuente de la App"
                          onChange={e => setConfig(c => ({ ...c, font_family: e.target.value }))}>
                          {FONT_OPTIONS.map(f => (
                            <MenuItem key={f.value} value={f.value} sx={{ fontFamily: f.value }}>
                              {f.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: config.font_family, fontSize: 24, fontWeight: 700 }}>
                          Vista previa de la fuente
                        </Typography>
                        <Typography sx={{ fontFamily: config.font_family, fontSize: 16 }}>
                          ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {tab === 3 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Pantallas Visibles en la App</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Activa o desactiva las pantallas que cada empresa puede ver en su aplicación móvil.
                      </Typography>
                      <Grid container spacing={1}>
                        {SCREEN_CONFIGS.map(s => (
                          <Grid item xs={12} sm={6} key={s.key}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={config.app_screens_config[s.key] !== false}
                                    onChange={e => setConfig(c => ({
                                      ...c,
                                      app_screens_config: { ...c.app_screens_config, [s.key]: e.target.checked }
                                    }))}
                                  />
                                }
                                label={s.label}
                              />
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
                      {saving ? 'Guardando...' : 'Guardar Configuración'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
